import React, { FunctionComponent, useEffect, useState } from 'react'
import { useTranslation } from 'next-i18next'
import styles from './style.module.css'
import { generateCreditLimitList } from './utils'
import { Button, Heading, Loader, Text } from '@afterpaytouch/core'
import { DropdownItems } from '@afterpaytouch/form'
import { setAssessmentRequest, useAppDispatch, useAppRouter, useCheckoutState, usePreAssessmentMutation } from '../../state'
import { Route } from '../../config/router'
import clsx from 'clsx'
import { useMoney } from '../Money/useMoney'

export const AffordabilityCreditLimit: FunctionComponent = () => {
  const { t } = useTranslation()
  const [selectedAmount, setSelectedAmount] = useState<DropdownItems | undefined>()
  const [creditLimitList, setCreditLimitList] = useState<DropdownItems[]>([])
  const [lock, setLock] = useState<boolean>(false)
  const state = useCheckoutState()
  const { locale, push } = useAppRouter()
  const dispatch = useAppDispatch()
  const [_, { data: preAssessmentData }] = usePreAssessmentMutation({ fixedCacheKey: 'shared-pre-assessment' })
  const testNameSpace = 'affordability-creditLimit'

  const orderAmount = state.started?.consumerAmount ?? state.checkout?.amount
  const { formatMoney } = useMoney({
    locale,
    currency: orderAmount?.currency,
    decimalPlaces: 0,
  })
  const shouldShowLoader = creditLimitList.length === 0

  const handleSelect = (amount: DropdownItems): void => {
    setSelectedAmount(amount)
  }

  useEffect(() => {
    if (orderAmount != null && preAssessmentData?.facilityLimit != null && formatMoney && creditLimitList.length === 0) {
      const creditLimitList: DropdownItems[] = generateCreditLimitList(orderAmount, preAssessmentData?.facilityLimit, formatMoney)
      setCreditLimitList(creditLimitList)
      setSelectedAmount(creditLimitList[0])
    }
  }, [orderAmount, preAssessmentData?.facilityLimit, formatMoney, creditLimitList])

  const onSubmit = async (): Promise<void> => {
    setLock(true)
    dispatch(setAssessmentRequest({ maxCreditLimit: selectedAmount?.key }))
    push(Route.AFFORDABILITY_INCOME)
  }

  return (
    <>
      {shouldShowLoader ? (
        <Loader text={t('consumerLending:affordability:creditLimit:loader')} />
      ) : (
        <div className={styles.limitContainer}>
          <Heading size='XXL'>{t('consumerLending:affordability:creditLimit:heading')}</Heading>
          <div className={styles.subheading}>
            <Text>{t('consumerLending:affordability:creditLimit:subheading')}</Text>
          </div>
          <div className={styles.buttonContainer}>
            {creditLimitList.map((limit) => (
              <div
                className={clsx(styles.limitButtons, selectedAmount === limit && styles.selected)}
                key={limit.key}
                data-testid={`${testNameSpace}-${limit.key}`}
                onClick={() => handleSelect(limit)}
              >
                <div className={styles.limitLabel}>
                  <Text size='L' bold={selectedAmount === limit}>
                    {limit.label}
                  </Text>
                </div>
              </div>
            ))}
          </div>
          <div className={styles.limitContinue}>
            <Text size='XXS' color='Gray40'>
              {t('consumerLending:affordability:creditLimit:disclaimer')}
            </Text>
          </div>
          <div className={styles.limitContinue}>
            <Button
              testNameSpace={testNameSpace}
              disabled={typeof selectedAmount === 'undefined'}
              loading={lock}
              padding='Fluid'
              kind='Primary'
              onClick={onSubmit}
            >
              {t('consumerLending:affordability:creditLimit:cta')}
            </Button>
          </div>
        </div>
      )}
    </>
  )
}
